import type { Pin } from './components/PinMap.tsx';
import type { ItineraryFacilityOption } from './api/itinerary.ts';

/**
 * The visit itinerary's map view (Arthur, 21 October): one pin per building with clients due, its name and "N due",
 * never a client's name. A building already added is drawn as a stop, numbered in the itinerary's own order, which
 * the up and down buttons change. A building with no coordinates is not drawn, only counted; it stays in the list.
 * A pin's click opens the building's panel (components/BuildingPanel.ts) and never adds (A-#41, 22 October).
 */

export type ItineraryView = 'list' | 'map';

/** Where this viewer's last choice is kept: this browser only, never the server or the address. */
export const VIEW_KEY = 'procura.itinerary.view';

/** The remembered choice, or List when there is none or storage cannot be read. */
export function readView(storage: () => Pick<Storage, 'getItem'> = () => localStorage): ItineraryView {
  try {
    return storage().getItem(VIEW_KEY) === 'map' ? 'map' : 'list';
  } catch {
    return 'list';
  }
}

export function saveView(view: ItineraryView, storage: () => Pick<Storage, 'setItem'> = () => localStorage): void {
  try {
    storage().setItem(VIEW_KEY, view);
  } catch {
    // Storage refused (a private window, blocked site data): the choice simply is not remembered.
  }
}

function placed(f: ItineraryFacilityOption): boolean {
  if (f.latitude === null || f.longitude === null) return false;
  return Number.isFinite(Number(f.latitude)) && Number.isFinite(Number(f.longitude));
}

/**
 * The pins, and how many buildings could not be drawn. `stops` is the itinerary's buildings in order; a building in
 * it is kind 'stop' with its number as the badge, every other is kind 'due'.
 */
export function itineraryPins(facilities: readonly ItineraryFacilityOption[], stops: readonly string[]): { pins: Pin[]; unmapped: number } {
  const pins: Pin[] = [];
  let unmapped = 0;
  for (const f of facilities) {
    if (!placed(f)) { unmapped += 1; continue; }
    const at = stops.indexOf(f.id);
    pins.push({
      id: f.id, latitude: f.latitude, longitude: f.longitude, title: f.facility_name, detail: hoverCounts(f),
      kind: at < 0 ? 'due' : 'stop', ...(at < 0 ? {} : { badge: String(at + 1) }),
    });
  }
  return { pins, unmapped };
}

/** The hover's second line: counts only, never a name (A-#54). */
export function hoverCounts(f: Pick<ItineraryFacilityOption, 'due' | 'up_to_date'>): string {
  return `${f.due} due \u00b7 ${f.up_to_date} up to date`;
}

export function unmappedLine(n: number): string | null {
  return n === 0 ? null : `${n} ${n === 1 ? 'building is' : 'buildings are'} not on the map (no location on record). Use the list for ${n === 1 ? 'it' : 'them'}.`;
}

/** A building's stop number in the itinerary (1 for the first), or null when it is not in it. */
export function stopNumber(stops: readonly string[], id: string): number | null {
  const at = stops.indexOf(id);
  return at < 0 ? null : at + 1;
}

interface Stop { detail: { facility: { id: string } } }

/** The building added as the last stop; one already in the itinerary is left where it is. */
export function withStop<G extends Stop>(groups: readonly G[], group: G): G[] {
  return groups.some((g) => g.detail.facility.id === group.detail.facility.id) ? groups.slice() : [...groups, group];
}

/** The building taken out of the itinerary; the others keep their order, so their numbers close up. */
export function withoutStop<G extends Stop>(groups: readonly G[], id: string): G[] {
  return groups.filter((g) => g.detail.facility.id !== id);
}

/** One stop's checklist choice changed (A-#54): that stop only, the others and the order as they were. */
export function withEveryone<G extends Stop & { everyone: boolean }>(groups: readonly G[], id: string, everyone: boolean): G[] {
  return groups.map((g) => (g.detail.facility.id === id ? { ...g, everyone } : g));
}
